import { Box, IconButton, Typography, Button, Grid2 as Grid } from '@mui/material';
import DataTable from '@/components/Common/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import Switch from 'components/Common/Switch';
import TimePicker from 'components/Common/TimePicker';
import { MdOutlineContentCopy } from 'react-icons/md';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import { RiDeleteBin6Line } from 'react-icons/ri';
import FormSchemaParser from '@/components/Common/FormSchemaParser';
import Input from '@/components/Common/Input';
import Select from '@/components/Common/Select';

interface ClioCalendarFormProps {
  namePrefix: string;
  control: any;
  getValues: any;
  setValue: any;
  disabled?: boolean;
  configFields: any;
  spacing: string;
  submitForm?: any;
}

const normalizeSelectOptions = (options: unknown) => {
  if (Array.isArray(options)) return options;
  return [];
};

const daysOfWeek = [
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Saturday', value: 'saturday' },
  { label: 'Sunday', value: 'sunday' }
];

const defaultHours = () => {
  const obj: any = {};
  daysOfWeek.forEach((day) => {
    obj[day.value] = {
      open: true,
      from_time: '',
      to_time: '',
      services: []
    };
  });
  return obj;
};

const emptyService = { id: '', duration: '' };

const ClioCalendarForm = ({
  namePrefix,
  control,
  disabled,
  configFields,
  spacing,
  submitForm,
  getValues,
  setValue
}: ClioCalendarFormProps) => {
  const calendarField = configFields?.find((field: any) => field.key === 'calendar_id');
  const servicesField = configFields?.find((field: any) => field.key === 'services');
  const serviceSelectField = servicesField?.group?.find((field: any) => field.key === 'id');
  const eventTypeOptions = normalizeSelectOptions(serviceSelectField?.options);
  const otherFields = configFields?.filter((field: any) => field.key !== 'calendar_id' && field.key !== 'services');

  const {
    fields: services,
    append: addService,
    remove: removeService
  } = useFieldArray({
    control,
    name: `${namePrefix}.services`
  });

  const watchedServices = useWatch({
    control,
    name: `${namePrefix}.services`
  });

  useEffect(() => {
    const existingServices = getValues(`${namePrefix}.services`);
    const existingHours = getValues(`${namePrefix}.bookable_hours`);

    if (!existingServices || existingServices.length === 0) {
      addService(emptyService);
    }

    if (!existingHours) {
      setValue(`${namePrefix}.bookable_hours`, defaultHours());
    }
  }, [addService, getValues, namePrefix, setValue]);

  const serviceOptions = (watchedServices || []).flatMap((service: any) => {
    if (!service?.id) return [];

    const match = eventTypeOptions.find((option: any) => String(option.value) === String(service.id));
    return match ? [{ label: match.label, value: String(service.id) }] : [];
  });

  const calendarOptions = normalizeSelectOptions(calendarField?.options);

  const columns = useMemo(
    (): ColumnDef<any>[] => [
      {
        header: 'DAY',
        accessorKey: 'label'
      },
      {
        header: 'STATUS',
        cell: (info) => {
          const day = info.row.original.value;
          return <Switch disabled={disabled} name={`${namePrefix}.bookable_hours.${day}.open`} control={control} />;
        }
      },
      {
        header: 'TIME',
        cell: (info) => {
          const day = info.row.original.value;
          return (
            <Box display='flex' gap={2} flexWrap='wrap'>
              <TimePicker
                name={`${namePrefix}.bookable_hours.${day}.from_time`}
                control={control}
                disabled={disabled}
              />
              <TimePicker name={`${namePrefix}.bookable_hours.${day}.to_time`} control={control} disabled={disabled} />
            </Box>
          );
        }
      },
      {
        header: 'SERVICE',
        cell: (info) => {
          const day = info.row.original.value;
          return (
            <Select
              label='Services'
              name={`${namePrefix}.bookable_hours.${day}.services`}
              control={control}
              disabled={disabled}
              options={serviceOptions}
              multiple
              fullWidth
            />
          );
        }
      },
      {
        header: 'ACTION',
        cell: (info) => (
          <CopyAction
            dayKey={info.row.original.value}
            disabled={disabled}
            fieldName={`${namePrefix}.bookable_hours`}
            getValues={getValues}
            setValue={setValue}
          />
        )
      }
    ],
    [control, disabled, getValues, namePrefix, serviceOptions, setValue]
  );

  return (
    <>
      {otherFields?.length > 0 && (
        <FormSchemaParser
          disabled={disabled}
          fields={otherFields}
          control={control}
          namePrefix={namePrefix}
          gridSize={12}
          spacing={spacing}
          submitForm={(val: any) => submitForm?.(val)}
        />
      )}

      <Box mb={3}>
        <Select
          label={calendarField?.label || 'Select Calendar'}
          name={`${namePrefix}.calendar_id`}
          control={control}
          disabled={disabled}
          options={calendarOptions}
          required={calendarField?.required}
          fullWidth
        />
      </Box>

      {servicesField && (
        <Box mb={3}>
          <Box display='flex' justifyContent='space-between' mb={2}>
            <Typography fontWeight={500}>Services</Typography>
            <Button disabled={disabled} size='small' variant='contained' onClick={() => addService(emptyService)}>
              Add Service
            </Button>
          </Box>

          {services.map((service: any, sIndex: number) => {
            const servicePrefix = `${namePrefix}.services.${sIndex}`;
            const selectedServiceIds = (watchedServices || [])
              .map((item: any, i: number) => (i === sIndex ? null : item?.id))
              .filter(Boolean)
              .map(String);
            const availableOptions = eventTypeOptions.filter(
              (option: any) => !selectedServiceIds.includes(String(option.value))
            );

            return (
              <Grid container spacing={2} alignItems='center' mb={2} key={service.id}>
                <Grid size={{ xs: 12, md: 5 }}>
                  <Select
                    name={`${servicePrefix}.id`}
                    control={control}
                    label={serviceSelectField?.label || 'Service'}
                    options={availableOptions}
                    disabled={disabled}
                    required={serviceSelectField?.required}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <Input
                    name={`${servicePrefix}.duration`}
                    control={control}
                    label='Service Duration (minutes)'
                    placeholder='e.g. 30'
                    type='number'
                    disabled={disabled}
                    fullWidth
                  />
                </Grid>
                {services?.length > 1 && !disabled && (
                  <Grid size='auto'>
                    <IconButton color='error' onClick={() => removeService(sIndex)}>
                      <RiDeleteBin6Line />
                    </IconButton>
                  </Grid>
                )}
              </Grid>
            );
          })}
        </Box>
      )}

      <Typography fontWeight={500} mt={2} my={2}>
        Working Hours
      </Typography>

      <DataTable data={daysOfWeek} columns={columns} />
    </>
  );
};

export default ClioCalendarForm;

const CopyAction = ({ dayKey, fieldName, getValues, setValue, disabled }: any) => {
  const handleCopy = () => {
    const source = getValues(`${fieldName}.${dayKey}`);

    daysOfWeek.forEach((day) => {
      const target = `${fieldName}.${day.value}`;
      setValue(`${target}.open`, source?.open);
      setValue(`${target}.from_time`, source?.from_time);
      setValue(`${target}.to_time`, source?.to_time);
      setValue(`${target}.services`, source?.services);
    });
  };

  return (
    <IconButton onClick={handleCopy} disabled={disabled}>
      <MdOutlineContentCopy size={18} />
    </IconButton>
  );
};
